import { useState, useEffect } from 'react';
import { Tag, Package, Layers, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useLineItemExclusions } from '../hooks/useLineItemExclusions';
import { ShopifyMultiSelectPicker, type PickerSelection } from './ShopifyMultiSelectPicker';
import type { MatchField } from '@threadheads/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  locationId: string | null;
  /** Called with the IDs of newly-inserted rule rows so the parent can flash them */
  onCreated?: (ruleIds: string[]) => void;
}

const FIELD_OPTIONS: Array<{
  id: MatchField;
  labelKey: string;
  descriptionKey: string;
  icon: typeof Tag;
}> = [
  {
    id: 'shopify_product_tag',
    labelKey: 'exclusions.field.tag',
    descriptionKey: 'exclusions.field.tagDescription',
    icon: Tag,
  },
  {
    id: 'shopify_product_type',
    labelKey: 'exclusions.field.productType',
    descriptionKey: 'exclusions.field.productTypeDescription',
    icon: Package,
  },
  {
    id: 'shopify_product_id',
    labelKey: 'exclusions.field.product',
    descriptionKey: 'exclusions.field.productDescription',
    icon: Layers,
  },
  // Variant-level rules removed per user feedback: tag / product type /
  // product cover the real exclusion needs and the variant picker added
  // confusion. Existing variant-scoped rules in the DB still match at
  // runtime — we just don't surface variant as a new-rule option.
];

export function AddRuleModal({ open, onClose, locationId, onCreated }: Props) {
  const { t } = useTranslation('settings');
  const { createBatchAsync, createBatchPending, fetchPreview } =
    useLineItemExclusions(locationId);

  const [step, setStep] = useState<1 | 2>(1);
  // Initialize with '' rather than null so the Radix RadioGroup is controlled
  // from the first render. A separate `hasField` flag tracks whether the user
  // actually picked something, since '' is also the "no selection" value.
  const [matchField, setMatchField] = useState<MatchField | ''>('');
  const hasField = matchField !== '';
  const [selections, setSelections] = useState<PickerSelection[]>([]);
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewTruncated, setPreviewTruncated] = useState(false);

  const resetAndClose = () => {
    setStep(1);
    setMatchField('');
    setSelections([]);
    setPreviewCount(null);
    setPreviewTruncated(false);
    onClose();
  };

  // Refresh preview when the first selection changes in step 2.
  useEffect(() => {
    if (!open || step !== 2 || !hasField || selections.length === 0) {
      setPreviewCount(null);
      setPreviewTruncated(false);
      return;
    }
    let cancelled = false;
    const first = selections[0];
    if (!first) return;
    fetchPreview({
      match_field: matchField,
      value: first.value,
      location_id: locationId ?? null,
    })
      .then((res) => {
        if (cancelled) return;
        setPreviewCount(res.matchCount);
        setPreviewTruncated(res.truncated);
      })
      .catch(() => {
        if (cancelled) return;
        setPreviewCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, step, hasField, matchField, selections, locationId, fetchPreview]);

  const handleSave = async () => {
    if (!hasField || selections.length === 0) return;
    const result = await createBatchAsync({
      match_field: matchField,
      selections,
      location_id: locationId ?? null,
      priority: 100,
      is_active: true,
    });
    onCreated?.(result.rules.map((r) => r.id));
    resetAndClose();
  };

  // Back keeps selections so a user can peek at step-1 descriptions without
  // losing their picks; switching match_field in step 1 explicitly clears.
  const back = () => {
    setStep(1);
  };

  const selectField = (next: MatchField) => {
    if (hasField && matchField !== next) setSelections([]);
    setMatchField(next);
  };

  const scopeLabel = locationId
    ? t('exclusions.modal.locationScope')
    : t('exclusions.modal.orgScope');

  const step2Label =
    hasField && t(`exclusions.field.fieldLabel.${matchField}`);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && resetAndClose()}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>
            {step === 1
              ? t('exclusions.modal.addTitle')
              : t('exclusions.modal.step2Title', { label: step2Label ?? '' })}
          </DialogTitle>
          <DialogDescription>{scopeLabel}</DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <RadioGroup
            value={matchField}
            onValueChange={(v) => selectField(v as MatchField)}
            className="grid gap-2 py-2"
          >
            {FIELD_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const isActive = matchField === opt.id;
              const inputId = `exclusion-field-${opt.id}`;
              return (
                <label
                  key={opt.id}
                  htmlFor={inputId}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    isActive ? 'border-primary bg-accent' : 'border-border',
                  )}
                >
                  <RadioGroupItem
                    value={opt.id}
                    id={inputId}
                    className="mt-0.5 shrink-0"
                  />
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <div className="font-medium">{t(opt.labelKey)}</div>
                    <div className="text-sm text-muted-foreground">
                      {t(opt.descriptionKey)}
                    </div>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
        ) : hasField ? (
          <div className="space-y-3 py-2">
            <ShopifyMultiSelectPicker
              matchField={matchField}
              selections={selections}
              onChange={setSelections}
            />
            <p className="text-xs text-muted-foreground">
              {t('exclusions.modal.multiSelectHint')}
            </p>
            {previewCount !== null && selections[0] ? (
              <div className="rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
                {previewCount === 0
                  ? t('exclusions.modal.previewImpactZero', {
                      label: selections[0].display_label,
                      defaultValue: `No open items currently match "${selections[0].display_label}".`,
                    })
                  : t('exclusions.modal.previewImpact', {
                      count: previewCount,
                      label: selections[0].display_label,
                      defaultValue: `Based on "${selections[0].display_label}" alone, this rule would skip ${previewCount} open item${previewCount === 1 ? '' : 's'}.`,
                    })}
                {previewTruncated ? (
                  <> {t('exclusions.modal.previewImpactTruncated')}</>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            {step === 2 ? (
              <Button type="button" variant="ghost" onClick={back}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                {t('exclusions.actions.back')}
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={resetAndClose}>
              {t('exclusions.actions.cancel')}
            </Button>
            {step === 1 ? (
              <Button
                type="button"
                onClick={() => setStep(2)}
                disabled={!hasField}
              >
                {t('exclusions.actions.next')}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleSave}
                disabled={selections.length === 0 || createBatchPending}
              >
                {createBatchPending
                  ? t('exclusions.actions.saving')
                  : t('exclusions.actions.addCount', { count: selections.length })}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
